package com.mgacreative.hotel.domain.repository

import com.mgacreative.hotel.domain.model.Agency
import com.mgacreative.hotel.domain.model.AgencyAllotment
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.query.Columns
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext

interface AgencyRepository {
    suspend fun getAgencies(): List<Agency>
    suspend fun getAgency(id: String): Agency?
    suspend fun createAgency(agency: Agency): Agency?
    suspend fun updateAgency(agency: Agency)
    suspend fun deleteAgency(id: String)
    
    suspend fun getAllotments(): List<AgencyAllotment>
    suspend fun getAllotment(id: String): AgencyAllotment?
    suspend fun createAllotment(allotment: AgencyAllotment): AgencyAllotment?
    suspend fun updateAllotment(allotment: AgencyAllotment)
    suspend fun deleteAllotment(id: String)
    
    suspend fun getActiveAllotmentsForDateRange(startDate: String, endDate: String): List<AgencyAllotment>
}

class AgencyRepositoryImpl(
    private val supabase: SupabaseClient
) : AgencyRepository {

    override suspend fun getAgencies(): List<Agency> = withContext(Dispatchers.Default) {
        supabase.postgrest["agencies"].select().decodeList<Agency>()
    }

    override suspend fun getAgency(id: String): Agency? = withContext(Dispatchers.Default) {
        supabase.postgrest["agencies"].select {
            filter { eq("id", id) }
        }.decodeSingleOrNull<Agency>()
    }

    override suspend fun createAgency(agency: Agency): Agency? = withContext(Dispatchers.Default) {
        supabase.postgrest["agencies"].insert(agency) {
            select()
        }.decodeSingleOrNull<Agency>()
    }

    override suspend fun updateAgency(agency: Agency) {
        withContext(Dispatchers.Default) {
            if (agency.id == null) return@withContext
            supabase.postgrest["agencies"].update(agency) {
                filter { eq("id", agency.id) }
            }
        }
    }

    override suspend fun deleteAgency(id: String) {
        withContext(Dispatchers.Default) {
            supabase.postgrest["agencies"].delete {
                filter { eq("id", id) }
            }
        }
    }

    override suspend fun getAllotments(): List<AgencyAllotment> = withContext(Dispatchers.Default) {
        supabase.postgrest["agency_allotments"].select(columns = Columns.raw("*, agencies(*)")).decodeList<AgencyAllotment>()
    }

    override suspend fun getAllotment(id: String): AgencyAllotment? = withContext(Dispatchers.Default) {
        supabase.postgrest["agency_allotments"].select(columns = Columns.raw("*, agencies(*)")) {
            filter { eq("id", id) }
        }.decodeSingleOrNull<AgencyAllotment>()
    }

    override suspend fun createAllotment(allotment: AgencyAllotment): AgencyAllotment? = withContext(Dispatchers.Default) {
        supabase.postgrest["agency_allotments"].insert(allotment) {
            select()
        }.decodeSingleOrNull<AgencyAllotment>()
    }

    override suspend fun updateAllotment(allotment: AgencyAllotment) {
        withContext(Dispatchers.Default) {
            if (allotment.id == null) return@withContext
            supabase.postgrest["agency_allotments"].update(allotment) {
                filter { eq("id", allotment.id) }
            }
        }
    }

    override suspend fun deleteAllotment(id: String) {
        withContext(Dispatchers.Default) {
            supabase.postgrest["agency_allotments"].delete {
                filter { eq("id", id) }
            }
        }
    }

    override suspend fun getActiveAllotmentsForDateRange(startDate: String, endDate: String): List<AgencyAllotment> = withContext(Dispatchers.Default) {
        supabase.postgrest["agency_allotments"].select(columns = Columns.raw("*, agencies(*)")) {
            filter {
                lte("start_date", endDate)
                gte("end_date", startDate)
            }
        }.decodeList<AgencyAllotment>()
    }
}
